import styled from "@emotion/styled";
import { FC, ReactNode, useEffect } from "react";

import {
  Button,
  DesignToken,
  Heading,
  ShadowToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { Icon, IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useWelcomeTutorialSteps } from "hooks/use-welcome-tutorial-steps";
import { withVar } from "themes/helpers";

import { WelcomeTutorialStep } from "../helpers";

import { StepCounter } from "./StepCounter";

interface IProps {
  step: WelcomeTutorialStep;
  title: string;
  description: string | ReactNode;
  required?: boolean;
  nextButtonText?: string;
  allowNext?: boolean;
  isNextLoading?: boolean;
  onNext: () => void;
  hideBackButton?: boolean;
  onBack?: () => void;
  children: ReactNode;
}

const StepScreen: FC<IProps> = ({
  step,
  title,
  description,
  required = false,
  nextButtonText = "Next",
  allowNext = true,
  onNext,
  isNextLoading,
  children,
  hideBackButton,
  onBack,
}) => {
  const steps = useWelcomeTutorialSteps();
  const isOnlyStep = steps.length === 1;
  const showBackButton = !hideBackButton && steps.indexOf(step) > 0;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Prevent moving forward when target is a Floating UI focusable element (such as DS Picker)
      const isTargetFloatingUiFocusable = (e.target as HTMLElement)?.dataset.floatingUiFocusable !== undefined;
      if (e.key === "Enter" && allowNext && !isTargetFloatingUiFocusable) {
        trackEngagement("Welcome tutorial: Enter key used to proceed", EventPlace.WelcomeTutorial, {
          currentStep: step,
        });
        onNext();
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return (): void => document.removeEventListener("keydown", handleKeyDown);
  }, [step, allowNext]);

  return (
    <Wrapper>
      <Header>
        <Icon name={IconsEnum.HelpDeskFullLogo} height="24px" />
      </Header>
      <Content>
        <StepInfo>
          {!isOnlyStep && <StepCounter step={step} />}
          <Heading size="xl" customColor={withVar(DesignToken.ContentBasicPrimary)}>
            {title}
          </Heading>
          <Heading size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)}>
            {description}
          </Heading>
        </StepInfo>
        <Interaction>{children}</Interaction>
      </Content>
      <Footer>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          {required ? "Required step" : "You can complete this step later"}
        </Text>
        <Flex gap={withVar(SpacingToken.Spacing2)}>
          {showBackButton && (
            <Button kind="secondary" onClick={onBack} size="large" data-testid="back-button">
              Back
            </Button>
          )}
          <Button
            kind="primary"
            onClick={onNext}
            size="large"
            disabled={!allowNext}
            loading={isNextLoading}
            type="button"
            data-testid="next-button"
          >
            {nextButtonText}
          </Button>
        </Flex>
      </Footer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100dvh;
  width: 100%;
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
`;

const Header = styled.div`
  padding: ${withVar(SpacingToken.Spacing5)};
  padding-bottom: ${withVar(SpacingToken.Spacing8)};
`;

const Content = styled.div`
  flex: 1;
  flex-wrap: wrap;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 ${withVar(SpacingToken.Spacing5)};
  overflow: hidden;

  align-content: flex-start;
  padding-top: 0;
  gap: ${withVar(SpacingToken.Spacing8)};
  overflow: auto;

  @media (min-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Tablet]}px) {
    padding-top: 25vh;
    flex-wrap: nowrap;
    gap: ${withVar(SpacingToken.Spacing16)};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
  padding: ${withVar(SpacingToken.Spacing5)};
  box-shadow: ${withVar(ShadowToken.DividerTop)};
  width: 100%;
  box-sizing: border-box;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};

  @media (min-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    padding-right: ${withVar(SpacingToken.Spacing20)};
  }
`;

const StepInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing4)};
  box-sizing: border-box;
  max-width: 500px;
  width: auto;
  max-height: 100%;
  height: 100%;

  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Tablet]}px) {
    width: 100%;
    height: auto;
  }
`;

const Interaction = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing4)};
  width: 100%;
  box-sizing: border-box;
  max-width: 530px;
  min-width: 300px;
  padding-bottom: ${withVar(SpacingToken.Spacing8)};

  @media (min-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Tablet]}px) {
    padding-right: 14px; // to display scrollbar outside of the content
    overflow: auto;
    height: 100%;
  }
`;

export default StepScreen;
